import React, { forwardRef, RefForwardingComponent, Ref } from 'react';
import { Trans } from 'react-i18next';
import { Theme, makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import MenuItem from '@material-ui/core/MenuItem';
import CancelIcon from '@material-ui/icons/Cancel';
import FilterBar from '../../../@common/FilterBar';
import FilterBarSelectInput from '../../../@common/FilterBar/FilterBarSelectInput';
import FilterBarSearchInput from '../../../@common/FilterBar/FilterBarSearchInput';
import FilterBarButton from '../../../@common/FilterBar/FilterBarButton';
import {
  ResourceFilterBarSelectOptions,
  ResourceFilterBarFormState,
  DefaultOption
} from '../../types';

const useStyles = makeStyles((theme: Theme) => {
  return {
    filterBarContainer: {
      textAlign: 'center',
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1.5),
      background: theme.palette.common.white
    },
    clearIcon: {
      fontSize: theme.typography.pxToRem(16),
      marginRight: theme.spacing(1)
    }
  };
});

type ResourcesFilterBarProps = {
  form: ResourceFilterBarFormState;
  handleChange: (name: string, value: string) => void;
  handleClearFilters: () => void;
  resourceOptions: { display: string; value: string }[];
  productOptions: string[];
  gradeOptions: string[];
} & ResourceFilterBarSelectOptions;

const ResourcesFilterBar: RefForwardingComponent<
  HTMLInputElement,
  ResourcesFilterBarProps
> = forwardRef((props, ref: Ref<HTMLInputElement>) => {
  const {
    resourceOptions = [],
    productOptions = [],
    gradeOptions = [],
    handleChange,
    handleClearFilters,
    form: {
      resource = DefaultOption.ALL,
      product = DefaultOption.ALL,
      grade = DefaultOption.ALL,
      search
    } = {}
  } = props;
  const classes = useStyles();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void =>
    handleChange(e.target.name, e.target.value);

  /**
   * This method is needed because the options are dynamically created, and if the value (i.e. from route params)
   * is not present in the options, error is thrown; defaults to 'ALL'
   */
  const hasValueInOptions = (entities: any[], value: string): boolean =>
    value === DefaultOption.ALL ||
    (entities.find(x => x === value || x?.value === value) ? true : false);

  return (
    <Container className={classes.filterBarContainer} maxWidth={false}>
      <FilterBar>
        <FilterBarSelectInput
          name={'resource'}
          value={hasValueInOptions(resourceOptions, resource) ? resource : DefaultOption.ALL}
          onChange={handleInputChange}
        >
          <MenuItem dense value={DefaultOption.ALL}>
            All Resources
          </MenuItem>
          {resourceOptions.map((x, idx: number) => (
            <MenuItem key={idx} dense value={x.value}>
              {x.display}
            </MenuItem>
          ))}
        </FilterBarSelectInput>
        <FilterBarSelectInput
          name={'product'}
          value={hasValueInOptions(productOptions, product) ? product : DefaultOption.ALL}
          onChange={handleInputChange}
        >
          <MenuItem dense value={DefaultOption.ALL}>
            All Products
          </MenuItem>
          {productOptions.map((x, idx: number) => (
            <MenuItem key={idx} dense value={x}>
              {x}
            </MenuItem>
          ))}
        </FilterBarSelectInput>
        <FilterBarSelectInput
          name={'grade'}
          value={hasValueInOptions(gradeOptions, grade) ? grade : DefaultOption.ALL}
          onChange={handleInputChange}
        >
          <MenuItem dense value={DefaultOption.ALL}>
            All Grades
          </MenuItem>
          {gradeOptions.map((x, idx: number) => (
            <MenuItem key={idx} dense value={x}>
              {x}
            </MenuItem>
          ))}
        </FilterBarSelectInput>
        <FilterBarSearchInput
          inputRef={ref}
          name={'search'}
          value={search || ''}
          onChange={handleInputChange}
        />
        <FilterBarButton onClick={handleClearFilters}>
          <CancelIcon className={classes.clearIcon} />
          <Trans>Clear All Filters</Trans>
        </FilterBarButton>
      </FilterBar>
    </Container>
  );
});
ResourcesFilterBar.displayName = 'ResourcesFilterBar';

export default ResourcesFilterBar;
